import { Component, computed, inject, OnInit } from '@angular/core';

import { AuthStore } from '../../features/authentication/store/auth.store';
import {
  ADMIN_PERMISSIONS,
  PermissionCheckerService,
} from '../../services/permission-checker.service';
import { TransactionCountsStore } from '../../stores/transaction-counts.store';
import { AppMenuitemComponent } from '../app-menuitem/app-menuitem.component';
import { MenuItem } from '../models/menu.model';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [AppMenuitemComponent],
  template: `
    <ul class="layout-menu">
      @for (item of menuModel(); track item.label; let i = $index) {
        <li app-menuitem [item]="item" [index]="i" [root]="true"></li>
      }
    </ul>
  `,
})
export class AppMenuComponent implements OnInit {
  private readonly authStore = inject(AuthStore);
  private readonly countsStore = inject(TransactionCountsStore);
  private readonly permissionChecker = inject(PermissionCheckerService);

  /**
   * Filter menu items based on permissions.
   * Items with a `permission` field are shown only if the user has that permission.
   * Groups with a `permissions` field are shown only if the user has any of those permissions.
   * Groups whose children all get filtered out are also hidden.
   */
  private filterItems(items: MenuItem[]): MenuItem[] {
    return items.reduce<MenuItem[]>((acc, item) => {
      // Leaf item with a single required permission
      if (item.permission) {
        if (this.permissionChecker.hasPermission(item.permission)) {
          acc.push(item);
        }
        return acc;
      }

      // Group item with children
      if (item.items) {
        // Check group-level permission gate (anyOf)
        if (item.permissions && !this.permissionChecker.hasAnyPermission(item.permissions)) {
          return acc;
        }

        // Filter children recursively
        const filteredChildren = this.filterItems(item.items);

        // Only include the group if it has visible children
        if (filteredChildren.length > 0) {
          acc.push({ ...item, items: filteredChildren });
        }
        return acc;
      }

      // Item with no permission requirement — always visible (e.g., root wrapper)
      acc.push(item);
      return acc;
    }, []);
  }

  readonly menuModel = computed<MenuItem[]>(() => {
    // Reactive dependency on permissions (forces recompute when permissions change)
    this.permissionChecker.permissions();

    const role = this.authStore.role();
    const isSuperAdmin = role === 'SuperAdmin';
    const isAdminOrAbove = role === 'Admin' || isSuperAdmin;

    // Build the raw menu tree with permission metadata
    const rawMenu: MenuItem[] = [
      {
        label: '',
        items: [
          // Dashboard — always visible to authenticated admin users
          {
            label: 'menu.dashboard',
            icon: 'pi pi-home',
            routerLink: '/dashboard',
            routerLinkActiveOptions: { exact: true },
            permission: ADMIN_PERMISSIONS.DASHBOARD_VIEW,
          },
          // Establishments
          {
            label: 'menu.establishments',
            icon: 'pi pi-building',
            routerLink: '/establishments',
            permission: ADMIN_PERMISSIONS.ESTABLISHMENTS_VIEW_ALL,
          },
          // POS Terminals
          {
            label: 'menu.posTerminals',
            icon: 'pi pi-tablet',
            routerLink: '/pos-terminals',
            permission: ADMIN_PERMISSIONS.POS_TERMINALS_VIEW,
          },
          // Payment Links
          {
            label: 'menu.paymentLinks',
            icon: 'pi pi-link',
            routerLink: '/payment-links',
            permission: ADMIN_PERMISSIONS.PAYMENT_LINKS_VIEW,
          },
          // Transactions
          {
            label: 'menu.transactions',
            icon: 'pi pi-credit-card',
            routerLink: '/transactions',
            permission: ADMIN_PERMISSIONS.TRANSACTIONS_VIEW,
          },
          // Chargebacks
          {
            label: 'menu.chargebacks',
            icon: 'pi pi-exclamation-triangle',
            routerLink: '/chargebacks',
            permission: ADMIN_PERMISSIONS.TRANSACTIONS_VIEW,
          },
          // Cancellations
          {
            label: 'menu.cancellations',
            icon: 'pi pi-times-circle',
            routerLink: '/cancellations',
            permission: ADMIN_PERMISSIONS.TRANSACTIONS_VIEW,
          },
          // Reports — SuperAdmin-only (only SuperAdmin has REPORTS_VIEW)
          {
            label: 'menu.reports',
            icon: 'pi pi-chart-bar',
            routerLink: '/reports',
            permission: ADMIN_PERMISSIONS.REPORTS_VIEW,
          },
          // Exchange Rates
          {
            label: 'menu.exchangeRates',
            icon: 'pi pi-dollar',
            routerLink: '/settings/exchange-rates',
            permission: ADMIN_PERMISSIONS.EXCHANGE_RATES_VIEW,
          },
          // Settings
          {
            label: 'menu.settings',
            icon: 'pi pi-cog',
            routerLink: '/settings',
            permission: ADMIN_PERMISSIONS.SETTINGS_VIEW,
          },
          // Notifications
          {
            label: 'menu.notifications',
            icon: 'pi pi-bell',
            routerLink: '/notifications',
            permission: ADMIN_PERMISSIONS.NOTIFICATIONS_SEND,
          },
          // Access Management — collapsible group (Admin/SuperAdmin only)
          ...(isAdminOrAbove
            ? [
                {
                  label: 'menu.accessManagement',
                  icon: 'pi pi-users',
                  permissions: [
                    ADMIN_PERMISSIONS.USERS_VIEW,
                    ADMIN_PERMISSIONS.ROLES_VIEW,
                    ADMIN_PERMISSIONS.PERMISSIONS_VIEW,
                  ],
                  items: [
                    {
                      label: 'menu.users',
                      icon: 'pi pi-users',
                      routerLink: '/users',
                      permission: ADMIN_PERMISSIONS.USERS_VIEW,
                    },
                    {
                      label: 'menu.roles',
                      icon: 'pi pi-shield',
                      routerLink: '/roles',
                      permission: ADMIN_PERMISSIONS.ROLES_VIEW,
                    },
                    {
                      label: 'menu.permissions',
                      icon: 'pi pi-key',
                      routerLink: '/permissions',
                      permission: ADMIN_PERMISSIONS.PERMISSIONS_VIEW,
                    },
                  ],
                },
              ]
            : []),
          // Monitor — collapsible group
          {
            label: 'menu.monitor',
            icon: 'pi pi-desktop',
            permissions: [
              ADMIN_PERMISSIONS.MONITOR_LOGS_VIEW,
              ADMIN_PERMISSIONS.MONITOR_STORAGE_VIEW,
              ADMIN_PERMISSIONS.MONITOR_DATABASE_VIEW,
              ADMIN_PERMISSIONS.SYSTEM_LOGS_VIEW,
              ADMIN_PERMISSIONS.AUDIT_LOGS_VIEW,
            ],
            items: [
              {
                label: 'menu.requestLogs',
                icon: 'pi pi-server',
                routerLink: '/monitor/logs',
                permission: ADMIN_PERMISSIONS.MONITOR_LOGS_VIEW,
              },
              {
                label: 'menu.logDashboard',
                icon: 'pi pi-chart-bar',
                routerLink: '/monitor/logs/dashboard',
                permission: ADMIN_PERMISSIONS.MONITOR_LOGS_VIEW,
              },
              {
                label: 'menu.storage',
                icon: 'pi pi-database',
                routerLink: '/monitor/storage',
                permission: ADMIN_PERMISSIONS.MONITOR_STORAGE_VIEW,
              },
              {
                label: 'menu.database',
                icon: 'pi pi-table',
                routerLink: '/monitor/database',
                permission: ADMIN_PERMISSIONS.MONITOR_DATABASE_VIEW,
              },
              {
                label: 'menu.systemLogs',
                icon: 'pi pi-file',
                routerLink: '/settings/system-logs',
                permission: ADMIN_PERMISSIONS.SYSTEM_LOGS_VIEW,
              },
              {
                label: 'menu.auditLogs',
                icon: 'pi pi-list',
                routerLink: '/compliance/audit-logs',
                permission: ADMIN_PERMISSIONS.AUDIT_LOGS_VIEW,
              },
            ],
          },
          // Compliance — collapsible group (Settlement only)
          {
            label: 'menu.compliance',
            icon: 'pi pi-shield',
            permissions: [ADMIN_PERMISSIONS.SETTLEMENT_VIEW],
            items: [
              {
                label: 'menu.settlement',
                icon: 'pi pi-chart-line',
                routerLink: '/compliance/settlement',
                permission: ADMIN_PERMISSIONS.SETTLEMENT_VIEW,
              },
            ],
          },
          // Reconciliation — collapsible group (Admin and SuperAdmin)
          ...(isAdminOrAbove
            ? [
                {
                  label: 'menu.reconciliation',
                  icon: 'pi pi-sync',
                  items: [
                    {
                      label: 'menu.reconciliationDashboard',
                      icon: 'pi pi-chart-bar',
                      routerLink: '/reconciliation/dashboard',
                    },
                    {
                      label: 'menu.providerReconciliation',
                      icon: 'pi pi-building',
                      routerLink: '/reconciliation/provider',
                    },
                    {
                      label: 'menu.providerTransactions',
                      icon: 'pi pi-list',
                      routerLink: '/reconciliation/provider-transactions',
                    },
                  ],
                },
              ]
            : []),
          // CommPix Test — SuperAdmin only
          ...(isSuperAdmin
            ? [
                {
                  label: 'CommPix Test',
                  icon: 'pi pi-flask',
                  routerLink: '/commpix-test',
                },
              ]
            : []),
        ],
      },
    ];

    // Apply permission filtering to the menu tree
    return this.filterItems(rawMenu);
  });

  ngOnInit(): void {
    if (this.countsStore.shouldRefresh()) {
      this.countsStore.refreshCounts();
    }
  }
}
